import { Box, Flex, Grid, Heading, Text, Link, Image, Button, useMediaQuery, Icon, Tabs, TabList, Tab, TabPanels, TabPanel, Accordion as ChakraAccordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon } from '@chakra-ui/react';
import { useState } from 'react';
import './style.css';


interface MarketplaceTileProps {
  href?: string;
  title: string;
  imageSrc: string;
  isEmpty?: boolean;
}

interface FeatureTileProps {
  title: string;
  content: string;
}

interface IntegrationData {
  title: string;
  subtitle: string;
  description: string;
  items: Array<{
    title: string;
    imageSrc: string;
    href?: string;
  }>;
}

// Components
const MarketplaceTile: React.FC<MarketplaceTileProps> = ({ href, title, imageSrc, isEmpty = false }) => {
  const ContentWrapper = href ? Link : Box;
  
  return (
    <ContentWrapper 
      href={href}
      display="flex"
      justifyContent="center"
      alignItems="center"
      position="relative"
      aspectRatio="1/1"
      p="10px"
      bg={isEmpty ? 'white' : 'var(--color-tile-gray)'}
      border={isEmpty ? '2px dashed var(--color-border-gray)' : '1px solid #eee'}
      _hover={{ 
        color: href ? 'var(--color-dark-text)' : undefined,
        bg: isEmpty ? undefined : (href ? '#fff' : undefined)
      }}
    >
      <Flex direction="column" align="center" justify="center" gap="10px">
        <Image src={imageSrc} alt={title} mixBlendMode="multiply" />
        {isEmpty && (
          <Text textAlign="center" fontSize="13.764px" fontWeight="600">
            {title}
          </Text>
        )}
      </Flex>
      {href && (
        <Box position="absolute" bottom="10px" right="10px" w="14px" h="14px">
          <Image src="https://www.idosell.com/panel/file-gate.php?id=596524&ext=.svg" alt="icon" />
        </Box>
      )}
    </ContentWrapper>
  );
};

const FeatureTile: React.FC<FeatureTileProps> = ({ title, content }) => (
  <Box
    bg="var(--color-tile-gray)"
    p="30px"
    display="flex"
    flexDirection="column"
    gap="15px"
  >
    <Heading
      fontSize={{ base: "24px", lg: "28px" }}
      fontWeight="600"
      lineHeight="1.2"
      position="relative"
      pb="30px"
      _after={{
        content: '""',
        position: 'absolute',
        bottom: '8px',
        left: '0',
        width: '30px',
        height: '3px',
        backgroundColor: '#171717'
      }}
    >
      {title}
    </Heading>
    <Text
      fontSize={{ base: "14px", lg: "16px" }}
      lineHeight="1.5"
    >
      {content}
    </Text>
  </Box>
);

const CustomSection: React.FC<{
  imageUrl: string;
  title: string;
  children: React.ReactNode;
  buttonText: string;
  buttonHref: string;
}> = ({ imageUrl, title, children, buttonText, buttonHref }) => (
  <Box position="relative" p={{ base: "40px 0 30px 0", lg: "100px 0" }}>
    <Box
      position="absolute"
      width="100vw"
      height="100%"
      top="0"
      left="50%"
      transform="translateX(-50%)"
      zIndex="-1"
      bgImage={`url(${imageUrl})`}
      bgSize="cover"
      bgPosition="center"
      display={{ base: "none", lg: "block" }}
    />
    <Flex
      className="marketplace__page__banner__content"
      maxW="1200px"
      mx="auto"
      direction={{ base: "column", lg: "row" }}
      justifyContent={{ base: "center", lg: "end" }}
      gap={{ base: "15px", lg: "50px" }}
      padding={{ base: "30px 0 0", md: "0", lg: "0 15px" }}
    >
      <Box
        bg="var(--color-green)"
        flex={{ lg: "0 0 50%" }}
        maxW={{ lg: "50%" }}
        p={{ base: "30px", lg: "50px" }}
        color="white"
      >
        <Flex direction="column" gap="20px">
          <Heading
            fontSize={{ base: "30px", lg: "34px" }}
            lineHeight={{ base: "37px", lg: "48px" }}
            fontWeight="600"
          >
            {title}
          </Heading>
          {children}
          <Box pt="16px">
            <Button
              as="a"
              href={buttonHref}
              bg="black"
              color="white"
              borderRadius="0"
              py="20px"
              minH="50px"
              px="30px"
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              gap="2"
              w="fit-content"
              _hover={{
                bg: 'white',
                color: 'black',
              }}
              transition=".3s"
              rightIcon={
                <Icon viewBox="0 0 9 14" w="9px" h="14px">
                  <path d="M0 13.6H2.70588L8.4 6.98565L2.70588 0.400024H0L5.69412 6.98565L0 13.6Z" fill="currentColor" />
                </Icon>
              }
            >
              {buttonText}
            </Button>
          </Box>
        </Flex>
      </Box>
    </Flex>
  </Box>
);

const TabContent: React.FC<{
  data: IntegrationData;
  isLargerThan757: boolean;
  menuItems: Array<{ title: string; href: string; }>;
}> = ({ data, isLargerThan757, menuItems }) => (
  <Grid
    templateColumns={{ base: "1fr", md: "minmax(auto, 370px) auto" }}
    gap="30px"
    p={{ 
      base: "0",
      md: "50px 15px", 
      lg: "50px 0px 100px" 
    }}
    className="marketplace__page__tabs__content"
  >
    <Box>
      <Flex direction="column" gap="15px" p={{ base: "30px 15px 0", md: "15px 0" }}>
        {isLargerThan757 && (
          <Heading as="h2" color="var(--color-green)" fontSize="34px" lineHeight="48px" fontWeight="600">
            {data.title}
          </Heading>
        )}
        <Text fontSize="18px" lineHeight="24px" fontWeight="400">
          {data.subtitle}
        </Text>
        <Text fontWeight="400" fontSize={{ base: "12px", lg: "12px" }} lineHeight={{ base: "16px", lg: "19px" }}>
          {data.description}
        </Text>
        {isLargerThan757 && (
          <Flex direction="column" gap="15px" pt="30px">
            {menuItems.map((item) => (
              <Link
                key={item.title}
                href={item.href}
                color="black"
                fontSize="16px"
                lineHeight="21px"
                fontWeight="600"
                textDecoration="underline"
                w="max-content"
                _hover={{ color: "#1AAC7A" }}
              >
                {item.title}
              </Link>
            ))}
          </Flex>
        )}
      </Flex>
    </Box>

    <Box width="100%" overflow="hidden" px="0px">
      <Grid
        templateColumns={{
          base: "repeat(2, 1fr)",
          sm: "repeat(3, 1fr)",
          md: "repeat(2, 1fr)",
          lg: "repeat(3, 1fr)",
          xl: "repeat(4, 1fr)"
        }}
        autoRows="1fr"
        gap={{ base: "15px", lg: "30px" }}
        justifyContent="center"
        p="15px 0"
      >
        {data.items.map((item) => (
          <MarketplaceTile
            key={item.title}
            href={item.href}
            title={item.title}
            imageSrc={item.imageSrc}
          />
        ))}
        <MarketplaceTile
          title="Nowe integracje już wkrótce"
          imageSrc="https://www.idosell.com/panel/file-gate.php?id=596518&ext=.svg"
          isEmpty
        />
      </Grid>
                </Box>
  </Grid>
);

const MarketplacePage: React.FC = () => {
  const [isLargerThan757] = useMediaQuery('(min-width: 757px)');
  const [tabIndex, setTabIndex] = useState(0);
  const [openPanels, setOpenPanels] = useState<number[]>([]);

  const handleAccordionChange = (expandedIndex: number | number[]) => {
    if (Array.isArray(expandedIndex)) {
      setOpenPanels(expandedIndex);
    }
  };

  const marketplaceData: IntegrationData = {
    title: "Integracje z marketplace'ami w IdoSell",
    subtitle: "Sprzedawaj wielokanałowo i rozwijaj sprzedaż tam, gdzie są Twoi klienci.",
    description: "Twoje produkty mogą trafić do milionów klientów, w Polsce i za granicą. Integracje IdoSell z największymi marketplace'ami pozwalają na szybkie uruchomienie sprzedaży w różnych kanałach jednocześnie. Sprzedawaj na Allegro, eBay, Amazon i wielu innych platformach, zarządzając wszystkim w jednym miejscu.",
    items: [
      { title: "allegro", imageSrc: "https://www.idosell.com/panel/file-gate.php?id=610493&ext=.svg", href: "https://www.idosell.com/pl/marketplaces-sprzedawaj-swoje-towary-w-wielu-miejscach-jednoczesnie-na-calym-swiecie/integracja-sklepu-internetowego-z-serwisem-aukcyjnym-allegro/" },
      { title: "amazon", imageSrc: "https://www.idosell.com/panel/file-gate.php?id=610494&ext=.svg", href: "https://www.idosell.com/pl/amazon/" },
      { title: "ebay", imageSrc: "https://www.idosell.com/panel/file-gate.php?id=610500&ext=.svg", href: "https://www.idosell.com/pl/zintegruj-sklep-internetowy-z-aukcjami-allegro-ebay-swistak/integracja-sklepu-internetowego-z-aukcjami-ebay/" },
      { title: "emag", imageSrc: "https://www.idosell.com/panel/file-gate.php?id=610501&ext=.svg", href: "https://www.idosell.com/pl/marketplaces-sprzedawaj-swoje-towary-w-wielu-miejscach-jednoczesnie-na-calym-swiecie/integracja-idosell-z-emag-marketplace/" },
      { title: "empik", imageSrc: "https://www.idosell.com/panel/file-gate.php?id=610508&ext=.svg", href: "https://www.idosell.com/pl/marketplaces-sprzedawaj-swoje-towary-w-wielu-miejscach-jednoczesnie-na-calym-swiecie/sprzedawaj-towary-poprzez-zintegrowany-empik-marketplace/" },
      { title: "erli", imageSrc: "https://www.idosell.com/panel/file-gate.php?id=610509&ext=.svg"},
      { title: "wish", imageSrc: "https://www.idosell.com/panel/file-gate.php?id=610513&ext=.svg", href: "https://pomoc.idosell.com/marketplaces/wish" },
      { title: "cdiscount", imageSrc: "https://www.idosell.com/panel/file-gate.php?id=610515&ext=.svg", href: "https://www.idosell.com/pl/marketplaces-sprzedawaj-swoje-towary-w-wielu-miejscach-jednoczesnie-na-calym-swiecie/cdiscount/" },
      { title: "spartoo", imageSrc: "https://www.idosell.com/panel/file-gate.php?id=610531&ext=.svg", href: "https://www.idosell.com/pl/marketplaces/spartoo/" },
      { title: "skroutz", imageSrc: "https://www.idosell.com/panel/file-gate.php?id=610532&ext=.svg", href: "https://www.idosell.com/pl/integracje-gotowe-do-wlaczenia-jednym-kliknieciem-/porownywarki-cen-serwisy-opinii-katalogi-produktow-generuj-oferty-bez-skladania-zamowien-w-wielu-miejscach-jednoczesnie/skroutz/" },
      { title: "ceneo", imageSrc: "https://www.idosell.com/panel/file-gate.php?id=610533&ext=.svg", href: "https://pomoc.idosell.com/marketplaces/ceneo-kup-teraz" },
      { title: "nethansa", imageSrc: "https://www.idosell.com/panel/file-gate.php?id=610553&ext=.svg", href: "https://www.idosell.com/pl/integracje-gotowe-do-wlaczenia-jednym-kliknieciem-/nethansa/" },
      { title: "morele", imageSrc: "https://www.idosell.com/panel/file-gate.php?id=610516&ext=.svg", href: "https://www.idosell.com/pl/marketplaces-sprzedawaj-swoje-towary-w-wielu-miejscach-jednoczesnie-na-calym-swiecie/darmowa-integracja-z-morele-net-marketplace/" },
      { title: "arena", imageSrc: "https://www.idosell.com/panel/file-gate.php?id=610517&ext=.svg", href: "https://www.idosell.com/pl/marketplaces-sprzedawaj-swoje-towary-w-wielu-miejscach-jednoczesnie-na-calym-swiecie/sprzedawaj-towary-poprzez-arena-pl-za-pomoca-modulu-marketplace-w-idosell/" },
      { title: "1but", imageSrc: "https://www.idosell.com/panel/file-gate.php?id=610518&ext=.svg", href: "https://pomoc.idosell.com/marketplaces/takemore-net" }
    ]
  };

  const priceComparisonData: IntegrationData = {
    title: "Integracje z porównywarkami cenowymi",
    subtitle: "Sprzedawaj wielokanałowo i rozwijaj sprzedaż tam, gdzie są Twoi klienci.",
    description: "Dotrzyj do klientów, którzy porównują oferty przed zakupem i zwiększ szanse na sprzedaż, oferując konkurencyjne ceny. Zbieraj opinie i zwiększaj wiarygodność swojego sklepu. W IdoSell możesz wygenerować ofertę dla porównywarek jednym kliknięciem, bez dodatkowej pracy.",
    items: [
      { title: "ceneo", imageSrc: "https://www.idosell.com/panel/file-gate.php?id=610533&ext=.svg", href: "https://www.idosell.com/pl/integracje-gotowe-do-wlaczenia-jednym-kliknieciem-/porownywarki-cen-serwisy-opinii-katalogi-produktow-generuj-oferty-bez-skladania-zamowien-w-wielu-miejscach-jednoczesnie/eksport-oferty-do-ceneo-w-idosell-to-proste/" },
      { title: "opineo", imageSrc: "https://www.idosell.com/panel/file-gate.php?id=610641&ext=.svg", href: "https://www.idosell.com/pl/marketing-internetowy-dla-sklepow-internetowych-idosell/opinie-twoich-klientow-w-sklepie-idosell-inspiracja-dla-ciebie-zrodlo-wiedzy-dla-twoich-klientow/opinie-opineo-pl-w-twoim-sklepie-internetowym/" },
      { title: "okazje", imageSrc: "https://www.idosell.com/panel/file-gate.php?id=610642&ext=.svg", href: "https://www.idosell.com/pl/integracje-gotowe-do-wlaczenia-jednym-kliknieciem-/porownywarki-cen-serwisy-opinii-katalogi-produktow-generuj-oferty-bez-skladania-zamowien-w-wielu-miejscach-jednoczesnie/integracja-z-okazje-info/" },
      { title: "e-mieszkanie", imageSrc: "https://www.idosell.com/panel/file-gate.php?id=610644&ext=.svg", href: "https://www.idosell.com/pl/integracje-gotowe-do-wlaczenia-jednym-kliknieciem-/porownywarki-cen-serwisy-opinii-katalogi-produktow-generuj-oferty-bez-skladania-zamowien-w-wielu-miejscach-jednoczesnie/integracja-sklepu-z-serwisem-e-mieszkanie-pl/" },
      { title: "gunstiger", imageSrc: "https://www.idosell.com/panel/file-gate.php?id=610646&ext=.svg", href: "https://www.idosell.com/pl/pl/integracje-gotowe-do-wlaczenia-jednym-kliknieciem-/porownywarki-katalogi-serwisy-opinii/guenstiger/" },
      { title: "geizhals", imageSrc: "https://www.idosell.com/panel/file-gate.php?id=610645&ext=.svg", href: "https://www.idosell.com/pl/integracje-gotowe-do-wlaczenia-jednym-kliknieciem-/porownywarki-cen-serwisy-opinii-katalogi-produktow-generuj-oferty-bez-skladania-zamowien-w-wielu-miejscach-jednoczesnie/geizhals/" },
      { title: "dobrekody", imageSrc: "https://www.idosell.com/panel/file-gate.php?id=610643&ext=.svg", href: "https://www.idosell.com/pl/integracje-gotowe-do-wlaczenia-jednym-kliknieciem-/bezplatna-promocja-sklepu-internetowego-w-serwisie-dobrekody-pl/" }
    ]
  };

  const menuItems = [
    { href: "https://www.idosell.com/pl/kurierzy/", title: "Kurierzy" },
    { href: "https://www.idosell.com/pl/systemy-platnosci-obslugiwane-w-idosell/", title: "Płatności" },
    { href: "https://www.idosell.com/pl/integracja-z-hurtowniami/", title: "Hurtownie" },
    { href: "https://www.idosell.com/pl/systemy-erp/", title: "Systemy ERP" },
    { href: "https://www.idosell.com/pl/mozliwosci-systemu-wms-w-sklepach-internetowych/obsluga-fulfillment/", title: "Fullfilment" },
    { href: "https://www.idosell.com/pl/marketing/", title: "Marketing" },
    { href: "https://www.idosell.com/pl/integracje/", title: "Inne" },
  ];

  return (
    <Box
      className="marketplace__page"
      maxW="1200px"
      mx="auto"
      fontFamily="'Poppins', Arial, sans-serif"
      fontStyle="normal"
      color="var(--color-dark-text)"
      sx={{
        '--color-green': '#1AAC7A',
        '--color-dark-text': '#171717',
        '--color-tile-gray': '#eee',
        '--color-border-gray': '#C8C8C8',
      }}
    >
      {/* Hero Section */}
      <Box 
        position="relative"
        display="flex"
        justifyContent="center"
        _before={{
          content: '""',
          position: 'absolute',
          backgroundColor: 'var(--color-green)',
          width: '100vw',
          height: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: -1,
        }}
      >
        <Flex
          maxW="1200px"
          direction={{ base: 'column', lg: 'row-reverse' }}
          align="center"
          w="100%"
        >
          <Box 
            flex={{ lg: '1 0 50%' }}
            display="flex"
            justifyContent="center"
            bg="var(--color-green)"
          >
            <picture>
              <source media="(min-width: 979px)" srcSet="https://www.idosell.com/panel/file-gate.php?id=610490&ext=.png" />
              <source media="(min-width: 757px)" srcSet="https://www.idosell.com/panel/file-gate.php?id=610491&ext=.png" />
              <Image 
                src="https://www.idosell.com/panel/file-gate.php?id=610491&ext=.png" 
                alt="Kurier z paczkami"
                w={{ base: '100vw', lg: 'auto' }}
                maxW={{ base: '100vw', lg: '100%' }}
                maxH={{ lg: '485px', xl: '585px' }}
              />
            </picture>
          </Box>

          <Flex
            className="marketplace__page__hero__content"
            flex={{ lg: '1 0 50%' }}
            direction="column"
            gap="15px"
            p={{ base: '30px 20px', lg: '10px 50px 15px 0px' }}
            bg="var(--color-green)"
            color="white"
          >
            <Heading
              as="h1"
              fontSize={{ base: '36px', lg: '36px', xl: '54px' }}
              lineHeight={{ base: '42px', lg: '48px', xl: '69px' }}
              textAlign={{ base: 'center', lg: 'left' }}
              fontWeight="600"
              m="0"
            >
              Gotowe integracje z porównywarkami cenowymi i marketplace'ami
            </Heading>
            <Text
              fontSize={{ base: '16px', lg: '18px' }}
              lineHeight={{ base: '24px', lg: 'normal' }}
              textAlign={{ base: 'center', lg: 'left' }}
            >
              Dotrzyj do milionów klientów na Allegro, Amazon, eBay i w porównywarkach cenowych. Zarządzaj ofertami bezpośrednio z panelu IdoSell i zautomatyzuj procesy sprzedaży.
                                </Text>
          </Flex>
        </Flex>
      </Box>

      {/* Features Section */}
      <Box p={{ base: "50px 15px", lg: "50px 0px 80px" }} className="marketplace__page__features">
        <Heading
          textAlign="center"
          fontSize={{ base: "30px", lg: "34px" }}
          lineHeight={{ base: "1.2", lg: "48px" }}
          fontWeight="600"
          mb={{ base: "30px", lg: "50px" }}
        >
          Sprzedawaj tam, gdzie są Twoi klienci
        </Heading>
        <Grid
          templateColumns={{
            base: "1fr",
            sm: "repeat(2, 1fr)",
            lg: "repeat(3, 1fr)"
          }}
          gap={{ base: "15px", lg: "30px" }}
          maxW="1200px"
          mx="auto"
        >
          <FeatureTile
            title="Możliwość dostosowania oferty pod każdy kanał"
            content="Sprzedawaj na dowolnym marketplace zarówno w Polsce, jak i za granicą – bez ograniczeń i dodatkowych opłat."
          />
          <FeatureTile
            title="Automatyczna aktualizacja cen i stanów magazynu"
            content="Zarządzaj sprzedażą z różnych marketplace'ów (np. Allegro, eBay, Amazon) z jednego panelu. Wszystkie zamówienia, stany magazynowe i ceny aktualizują się automatycznie dzięki integracji z WMS."
          />
          <FeatureTile
            title="Kompleksowa obsługa zamówień"
            content="Integracja z naszym wbudowanym systemem WMS sprawia, że realizacja zamówień jest szybka i wygodna. Otrzymujesz już opłacone zamówienia, które od razu możesz wysłać do klientów."
          />
        </Grid>
                </Box>

      {/* Marketplace Section */}
      {isLargerThan757 ? (
        <Box maxW="1200px" mx="auto" className="marketplace__page__tabs">
          <Tabs variant="enclosed" onChange={(index) => setTabIndex(index)}>
            <Flex justify="center">
              <TabList 
                borderBottom="1px solid var(--color-border-gray)"
                width="fit-content"
                mb="30px"
              >
                <Tab
                  px="20px"
                  py="20px"
                  fontSize="18px"
                  color="#171717"
                  _selected={{ 
                    color: "#171717",
                    borderBottom: "3px solid var(--color-green)",
                    fontWeight: "600"
                  }}
                >
                  Marketplace
                </Tab>
                <Tab
                  px="20px"
                  py="20px"
                  fontSize="18px"
                  color="#171717"
                  _selected={{ 
                    color: "#171717",
                    borderBottom: "3px solid var(--color-green)",
                    fontWeight: "600"
                  }}
                >
                  Porównywarki cenowe
                </Tab>
              </TabList>
            </Flex>
            <TabPanels>
              <TabPanel p={0}>
                <TabContent 
                  data={marketplaceData}
                  isLargerThan757={isLargerThan757}
                  menuItems={menuItems}
                />
              </TabPanel>
              <TabPanel p={0}>
                <TabContent 
                  data={priceComparisonData}
                  isLargerThan757={isLargerThan757}
                  menuItems={menuItems}
                />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Box>
      ) : (
        <ChakraAccordion 
          allowMultiple
          index={openPanels}
          onChange={handleAccordionChange}
        >
          <AccordionItem border="none">
            <AccordionButton 
              bg="var(--color-tile-gray)"
              _hover={{ bg: "var(--color-tile-gray)" }}
              py="15px"
              paddingLeft="15px"
              paddingRight="8px"
              mx="0"
              maxW="100%"
            >
              <Box flex="1" textAlign="left" fontWeight="600">
                Marketplace
              </Box>
              <AccordionIcon boxSize={8} />
            </AccordionButton>
            <AccordionPanel px={"15px"} paddingBottom="45px">
              <TabContent 
                data={marketplaceData}
                isLargerThan757={isLargerThan757}
                menuItems={menuItems}
              />
            </AccordionPanel>
          </AccordionItem>
          <AccordionItem border="none" mt="15px">
            <AccordionButton 
              bg="var(--color-tile-gray)"
              _hover={{ bg: "var(--color-tile-gray)" }}
              py="15px"
              paddingLeft="15px"
              paddingRight="8px"
              mx="0"
              maxW="100%"
            >
              <Box flex="1" textAlign="left" fontWeight="600">
                Porównywarki cenowe
                </Box>
              <AccordionIcon boxSize={8} />
            </AccordionButton>
            <AccordionPanel px={"15px"} paddingBottom="45px">
              <TabContent 
                data={priceComparisonData}
                isLargerThan757={isLargerThan757}
                menuItems={menuItems}
              />
            </AccordionPanel>
          </AccordionItem>
        </ChakraAccordion>
      )}

      {/* Banner Section */}
      <Box display={{ base: 'block' }}>
        <CustomSection
          imageUrl="https://www.idosell.com/panel/file-gate.php?id=610492&ext=.jpeg"
          title="Gotowe i zawsze aktualne integracje w IdoSell"
          buttonText="Dowiedz się więcej"
          buttonHref="https://www.idosell.com/pl/integracje/"
        >
          <Text fontWeight="600" fontSize="26px" lineHeight="34px" color="#171717">
            W IdoSell nie musisz szukać integracji na własną rękę ani martwić się o ich aktualizację. 
          </Text>
          <Text fontWeight="400" fontSize="16px" lineHeight="25px" color="#171717">
            Korzystaj z gotowych i wbudowanych połączeń z systemami płatności, kurierami, marketplace'ami, porównywarkami cen i serwisami opinii. Wszystko działa od ręki i bez dodatkowej konfiguracji.
          </Text>
        </CustomSection>
      </Box>

      {/* Contact Section */}
      <Flex
        direction="column"
        align="center"
        textAlign="center"
        p="55px 20px"
      >
                                <Heading
          as="h2"
          mb="15px"
          fontSize={{ base: "30px", lg: "34px" }}
          lineHeight={{ base: "37px", lg: "48px" }}
          fontWeight="600"
        >
          Brakuje integracji, której szukasz?
                                </Heading>
        <Text
          mb="30px"
          fontSize={{ base: "16px", lg: "18px" }}
        >
          Możemy dla Ciebie zrobić integrację na życzenie.
                                </Text>
        <Button
          as="a"
          href="https://csc.idosell.com/support/contact_form/597?title=Prace%20kontraktowe"
          maxW="350px"
          w="100%"
          bg="black"
          color="white"
          borderRadius="0"
          py="20px"
          minH="50px"
          px="30px"
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap="2"
          _hover={{
            bg: 'var(--color-green)',
            color: 'white',
          }}
          transition=".3s"
          rightIcon={
            <Icon viewBox="0 0 9 14" w="9px" h="14px">
              <path d="M0 13.6H2.70588L8.4 6.98565L2.70588 0.400024H0L5.69412 6.98565L0 13.6Z" fill="currentColor" />
            </Icon>
          }
        >
          Przejdź do biura obsługi klienta
        </Button>
      </Flex>
                </Box>
    );
};

export default MarketplacePage;